import React, { useState, useMemo } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  CartesianGrid
} from 'recharts';
import {
  TrendingUp,
  DollarSign,
  Layers,
  Leaf,
  Clock,
  PieChart as PieIcon,
  BarChart3,
  Sliders,
  ChevronDown,
  ChevronUp,
  Droplets,
  RefreshCw
} from 'lucide-react';
import { VdpLayout, VdpTemplate, CSVData } from '../../types';
import { 
  getAvailableSubstrates 
} from '../../utils/substratePresets';
import { SubstrateCustomizerModal } from './SubstrateCustomizerModal';
import { useRealtimeCurrency } from '../../hooks/useRealtimeCurrency';

interface ProductionRoiVisualizerProps {
  layout: VdpLayout;
  template: VdpTemplate;
  csvData: CSVData | null;
  selectedRecordsCount: number;
  totalPages: number;
  onUpdateLayout?: (key: keyof VdpLayout, value: any) => void;
}

const PAGE_SIZES: Record<string, { width: number; height: number }> = {
  A4: { width: 595, height: 842 },
  A3: { width: 842, height: 1190 },
  Letter: { width: 612, height: 792 },
  Tabloid: { width: 792, height: 1224 },
  Custom: { width: 600, height: 600 }
};

export const ProductionRoiVisualizer: React.FC<ProductionRoiVisualizerProps> = ({
  layout,
  template,
  csvData,
  selectedRecordsCount,
  totalPages,
  onUpdateLayout
}) => {
  const { activeCode, activeSymbol, supportedCurrencies, switchCurrency, exchangeRateString, refreshRates, isRefreshing } = useRealtimeCurrency();
  const [activeVisualTab, setActiveVisualTab] = useState<'costs' | 'yield' | 'ink' | 'scaling'>('costs');
  const [showCostSettings, setShowCostSettings] = useState(false);
  const [showSubstrateModal, setShowSubstrateModal] = useState(false);

  // Substrate presets
  const availableSubstrates = useMemo(() => getAvailableSubstrates(), [showSubstrateModal, layout.substrateType]);

  // Economic Parameters
  const currSym = activeSymbol || layout.currencySymbol || '₹';
  const paperCostPerSheet = layout.costPerSheet !== undefined ? layout.costPerSheet : 12;
  const inkCostPerSheet = layout.inkCostPerSheet !== undefined ? layout.inkCostPerSheet : 1.5;
  const laborRatePerHour = layout.laborRatePerHour !== undefined ? layout.laborRatePerHour : 300;
  const pressSpeedPpm = layout.pressSpeedPpm !== undefined ? layout.pressSpeedPpm : 20;

  // Geometry calculations
  const pageSizeObj = PAGE_SIZES[layout.pageSize] || PAGE_SIZES.A4;
  const isLandscape = layout.orientation === 'landscape';
  const sheetWidth = isLandscape ? pageSizeObj.height : pageSizeObj.width;
  const sheetHeight = isLandscape ? pageSizeObj.width : pageSizeObj.height;
  const totalSheetArea = sheetWidth * sheetHeight;

  const cols = Number(layout.cols) || 1;
  const rows = Number(layout.rows) || 1;
  const disabledCount = layout.disabledSlots?.length || 0;
  const activeSlotsPerSheet = Math.max(1, (cols * rows) - disabledCount);

  const stickerWidth = Number(template.width) || 100;
  const stickerHeight = Number(template.height) || 100;
  const singleStickerArea = stickerWidth * stickerHeight;
  const totalActivePrintedAreaPerSheet = activeSlotsPerSheet * singleStickerArea;

  const gapX = Number(layout.gapX) || 0;
  const gapY = Number(layout.gapY) || 0;
  const totalGutterAreaPerSheet = Math.max(0, ((cols - 1) * gapX * stickerHeight * rows) + ((rows - 1) * gapY * stickerWidth * cols));
  const activePrintPercent = totalSheetArea > 0 ? Math.min(100, Math.max(0, (totalActivePrintedAreaPerSheet / totalSheetArea) * 100)) : 0;
  const gutterWastePercent = totalSheetArea > 0 ? Math.min(100 - activePrintPercent, Math.max(0, (totalGutterAreaPerSheet / totalSheetArea) * 100)) : 0;
  const trimMarginWastePercent = Math.max(0, 100 - activePrintPercent - gutterWastePercent);

  // Production Volume
  const runQuantity = selectedRecordsCount > 0 ? selectedRecordsCount : 1000;
  const singleUpSheets = runQuantity;
  const multiUpSheets = totalPages > 0 ? totalPages : Math.ceil(runQuantity / activeSlotsPerSheet);
  const sheetsSaved = Math.max(0, singleUpSheets - multiUpSheets);

  // Press Time
  const singleUpPressMin = singleUpSheets / Math.max(1, pressSpeedPpm);
  const multiUpPressMin = multiUpSheets / Math.max(1, pressSpeedPpm);
  const pressTimeSavedMin = Math.max(0, singleUpPressMin - multiUpPressMin);

  // Cost Computations
  const singleUpPaperCost = singleUpSheets * paperCostPerSheet;
  const multiUpPaperCost = multiUpSheets * paperCostPerSheet;
  const paperSavings = Math.max(0, singleUpPaperCost - multiUpPaperCost);

  const singleUpInkCost = singleUpSheets * inkCostPerSheet;
  const multiUpInkCost = multiUpSheets * inkCostPerSheet;
  const inkSavings = Math.max(0, singleUpInkCost - multiUpInkCost);

  const singleUpLaborCost = (singleUpPressMin / 60) * laborRatePerHour;
  const multiUpLaborCost = (multiUpPressMin / 60) * laborRatePerHour;
  const laborSavings = Math.max(0, singleUpLaborCost - multiUpLaborCost);

  const singleUpTotalCost = singleUpPaperCost + singleUpInkCost + singleUpLaborCost;
  const multiUpTotalCost = multiUpPaperCost + multiUpInkCost + multiUpLaborCost;
  const totalSavings = Math.max(0, singleUpTotalCost - multiUpTotalCost);
  const savingsPct = singleUpTotalCost > 0 ? ((totalSavings / singleUpTotalCost) * 100).toFixed(1) : '0';

  // Environmental Impact
  const treesConserved = sheetsSaved / 8333;
  const co2AvoidedKg = sheetsSaved * 0.045;
  const waterSavedLiters = sheetsSaved * 1.3;

  // Chart Dataset 1: Cost Breakdown Comparison
  const costComparisonData = useMemo(() => [
    {
      category: 'Paper Substrate',
      '1-Up Baseline': Math.round(singleUpPaperCost),
      'Multi-Up Imposition': Math.round(multiUpPaperCost),
      Savings: Math.round(paperSavings)
    },
    {
      category: 'Ink / Toner Clicks',
      '1-Up Baseline': Math.round(singleUpInkCost),
      'Multi-Up Imposition': Math.round(multiUpInkCost),
      Savings: Math.round(inkSavings)
    },
    {
      category: 'Press Labor Time',
      '1-Up Baseline': Math.round(singleUpLaborCost),
      'Multi-Up Imposition': Math.round(multiUpLaborCost),
      Savings: Math.round(laborSavings)
    },
    {
      category: 'Total Production Budget',
      '1-Up Baseline': Math.round(singleUpTotalCost),
      'Multi-Up Imposition': Math.round(multiUpTotalCost),
      Savings: Math.round(totalSavings)
    }
  ], [singleUpPaperCost, multiUpPaperCost, paperSavings, singleUpInkCost, multiUpInkCost, inkSavings, singleUpLaborCost, multiUpLaborCost, laborSavings, singleUpTotalCost, multiUpTotalCost, totalSavings]);

  // Chart Dataset 2: Paper Surface Area Distribution (Pie Chart)
  const yieldPieData = useMemo(() => [
    { name: 'Active Printed Surface', value: parseFloat(activePrintPercent.toFixed(1)), color: '#10b981' },
    { name: 'Trim & Perimeter Margins', value: parseFloat(trimMarginWastePercent.toFixed(1)), color: '#64748b' },
    { name: 'Inter-Sticker Gutters', value: parseFloat(gutterWastePercent.toFixed(1)), color: '#94a3b8' }
  ], [activePrintPercent, trimMarginWastePercent, gutterWastePercent]);

  // Chart Dataset 3: Ink Usage & Impression Passes Comparison
  const inkEfficiencyData = useMemo(() => [
    {
      metric: 'Machine Passes',
      '1-Up (Single Pass)': singleUpSheets,
      'Multi-Up Optimized': multiUpSheets,
      unit: 'impressions'
    },
    {
      metric: 'Active Print Passes',
      '1-Up (Single Pass)': singleUpSheets,
      'Multi-Up Optimized': multiUpSheets,
      unit: 'passes'
    },
    {
      metric: 'Estimated Ink Cost',
      '1-Up (Single Pass)': Math.round(singleUpInkCost),
      'Multi-Up Optimized': Math.round(multiUpInkCost),
      unit: currSym
    }
  ], [singleUpSheets, multiUpSheets, singleUpInkCost, multiUpInkCost, currSym]);

  // Chart Dataset 4: Volume ROI Scaling Projection Curve
  const volumeCurveData = useMemo(() => {
    const steps = [100, 250, 500, 1000, 2500, 5000, 10000];
    return steps.map(qty => {
      const s1Sheets = qty;
      const sNSheets = Math.ceil(qty / activeSlotsPerSheet);
      const c1 = (s1Sheets * paperCostPerSheet) + (s1Sheets * inkCostPerSheet) + ((s1Sheets / Math.max(1, pressSpeedPpm) / 60) * laborRatePerHour);
      const cN = (sNSheets * paperCostPerSheet) + (sNSheets * inkCostPerSheet) + ((sNSheets / Math.max(1, pressSpeedPpm) / 60) * laborRatePerHour);
      const saved = Math.max(0, c1 - cN);
      return {
        quantity: qty.toLocaleString(),
        '1-Up Cost': Math.round(c1),
        'Imposition Cost': Math.round(cN),
        'Net Savings': Math.round(saved)
      };
    });
  }, [activeSlotsPerSheet, paperCostPerSheet, inkCostPerSheet, pressSpeedPpm, laborRatePerHour]);

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-md shadow-xs overflow-hidden flex flex-col gap-5 p-5">
      {/* Header with Visual Tabs & Settings Trigger */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-4 border-b border-zinc-800">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="bg-emerald-950/60 text-emerald-300 border border-emerald-800/60 text-xs font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full flex items-center gap-1">
              <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />
              Production ROI & Yield Analytics
            </span>
            <span className="text-xs text-zinc-500 font-medium">
              Based on {runQuantity.toLocaleString()} records • {activeSlotsPerSheet}-Up ({layout.pageSize})
            </span>
          </div>
          <h4 className="text-base font-bold text-zinc-200">
            Real-Time Cost Reduction, Ink & Paper Savings
          </h4>
        </div>

        {/* View Switcher Tabs */}
        <div className="flex items-center gap-1 bg-zinc-800 p-1 rounded-md">
          <button
            onClick={() => setActiveVisualTab('costs')}
            id="tab-roi-costs"
            className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
              activeVisualTab === 'costs'
                ? 'bg-zinc-900 text-blue-400 shadow-2xs'
                : 'text-zinc-400 hover:text-zinc-100'
            }`}
          >
            <BarChart3 className="w-3.5 h-3.5" />
            <span>Cost Breakdown</span>
          </button>

          <button
            onClick={() => setActiveVisualTab('yield')}
            id="tab-roi-yield"
            className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
              activeVisualTab === 'yield'
                ? 'bg-zinc-900 text-emerald-400 shadow-2xs'
                : 'text-zinc-400 hover:text-zinc-100'
            }`}
          >
            <PieIcon className="w-3.5 h-3.5" />
            <span>Paper Yield ({activePrintPercent.toFixed(0)}%)</span>
          </button>

          <button
            onClick={() => setActiveVisualTab('ink')}
            id="tab-roi-ink"
            className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
              activeVisualTab === 'ink'
                ? 'bg-zinc-900 text-purple-400 shadow-2xs'
                : 'text-zinc-400 hover:text-zinc-100'
            }`}
          >
            <Droplets className="w-3.5 h-3.5" />
            <span>Ink & Machine</span>
          </button>

          <button
            onClick={() => setActiveVisualTab('scaling')}
            id="tab-roi-scaling"
            className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
              activeVisualTab === 'scaling'
                ? 'bg-zinc-900 text-amber-400 shadow-2xs'
                : 'text-zinc-400 hover:text-zinc-100'
            }`}
          >
            <TrendingUp className="w-3.5 h-3.5" />
            <span>Volume Curve</span>
          </button>
        </div>
      </div>

      {/* Primary KPI Highlights Banner */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="p-3.5 bg-emerald-950/40 border border-emerald-900/50 rounded-md flex flex-col justify-between">
          <span className="text-[11px] font-bold uppercase text-emerald-400 flex items-center gap-1">
            <DollarSign className="w-3.5 h-3.5 text-emerald-400" />
            Total Net Savings
          </span>
          <div className="text-xl font-black text-emerald-300 font-mono mt-1">
            {currSym}{totalSavings.toLocaleString()}
          </div>
          <span className="text-[11px] font-semibold text-emerald-400 mt-0.5">
            {savingsPct}% Production Cost Saved
          </span>
        </div>

        <div className="p-3.5 bg-blue-950/40 border border-blue-900/50 rounded-md flex flex-col justify-between">
          <span className="text-[11px] font-bold uppercase text-blue-400 flex items-center gap-1">
            <Layers className="w-3.5 h-3.5 text-blue-400" />
            Stock Conserved
          </span>
          <div className="text-xl font-black text-blue-300 font-mono mt-1">
            {sheetsSaved.toLocaleString()} <span className="text-xs font-normal text-blue-400">sheets</span>
          </div>
          <span className="text-[11px] font-semibold text-blue-400 mt-0.5">
            {multiUpSheets} vs {singleUpSheets} sheets
          </span>
        </div>

        <div className="p-3.5 bg-purple-950/40 border border-purple-900/50 rounded-md flex flex-col justify-between">
          <span className="text-[11px] font-bold uppercase text-purple-400 flex items-center gap-1">
            <Clock className="w-3.5 h-3.5 text-purple-400" />
            Press Time Saved
          </span>
          <div className="text-xl font-black text-purple-300 font-mono mt-1">
            {pressTimeSavedMin >= 60 ? `${(pressTimeSavedMin / 60).toFixed(1)} hrs` : `${pressTimeSavedMin.toFixed(0)} min`}
          </div>
          <span className="text-[11px] font-semibold text-purple-400 mt-0.5">
            ~{currSym}{inkSavings.toFixed(0)} Ink Click Charges Saved
          </span>
        </div>

        <div className="p-3.5 bg-teal-950/40 border border-teal-900/50 rounded-md flex flex-col justify-between">
          <span className="text-[11px] font-bold uppercase text-teal-400 flex items-center gap-1">
            <Leaf className="w-3.5 h-3.5 text-teal-400" />
            Eco Impact
          </span>
          <div className="text-xl font-black text-teal-300 font-mono mt-1">
            {co2AvoidedKg.toFixed(1)} <span className="text-xs font-normal text-teal-400">kg CO₂e</span>
          </div>
          <span className="text-[11px] font-semibold text-teal-400 mt-0.5">
            {waterSavedLiters.toFixed(0)}L Water • {treesConserved.toFixed(3)} Trees
          </span>
        </div>
      </div>

      {/* Main Recharts Visualizer Area */}
      <div className="bg-zinc-950 border border-zinc-800/80 rounded-md p-4">
        {/* VIEW 1: Cost Breakdown Comparison */}
        {activeVisualTab === 'costs' && (
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between text-xs text-zinc-500 font-medium px-1">
              <span>Financial Comparison: 1-Up Baseline vs Current Imposition</span>
              <span className="text-emerald-700 font-bold">Net Project Benefit: +{currSym}{totalSavings.toLocaleString()}</span>
            </div>

            <div className="w-full h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={costComparisonData}
                  margin={{ top: 10, right: 20, left: 10, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis 
                    dataKey="category" 
                    tick={{ fontSize: 11, fill: '#475569', fontWeight: 600 }}
                    axisLine={{ stroke: '#cbd5e1' }}
                    tickLine={false}
                  />
                  <YAxis 
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(val) => `${currSym}${val}`}
                  />
                  <Tooltip 
                    formatter={(value: any, name: any) => [`${currSym}${Number(value).toLocaleString()}`, name]}
                    contentStyle={{ backgroundColor: '#0f172a', borderColor: '#334155', borderRadius: '12px', color: '#f8fafc', fontSize: '12px', fontWeight: 'bold' }}
                    itemStyle={{ color: '#e2e8f0' }}
                  />
                  <Legend 
                    wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }} 
                  />
                  <Bar dataKey="1-Up Baseline" fill="#94a3b8" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="Multi-Up Imposition" fill="#2563eb" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="Savings" fill="#10b981" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* VIEW 2: Paper Surface Area Distribution */}
        {activeVisualTab === 'yield' && (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
            <div className="md:col-span-7 w-full h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={yieldPieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={95}
                    paddingAngle={4}
                    dataKey="value"
                  >
                    {yieldPieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value: any, name: any) => [`${value}%`, name]}
                    contentStyle={{ backgroundColor: '#0f172a', borderColor: '#334155', borderRadius: '12px', color: '#f8fafc', fontSize: '12px', fontWeight: 'bold' }}
                  />
                  <Legend 
                    layout="horizontal" 
                    verticalAlign="bottom" 
                    align="center"
                    wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="md:col-span-5 space-y-3 p-3 bg-zinc-900 border border-zinc-800 rounded-md">
              <div className="text-xs font-bold text-zinc-300 uppercase tracking-wider">
                Sheet Surface Efficiency Rating
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-black text-blue-600 font-mono">
                  {activePrintPercent.toFixed(1)}%
                </span>
                <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200">
                  {activePrintPercent >= 70 ? 'High Yield' : activePrintPercent >= 50 ? 'Medium Yield' : 'Moderate Scrap'}
                </span>
              </div>
              <p className="text-xs text-zinc-500 leading-relaxed">
                Your layout nests <strong className="text-zinc-200">{activeSlotsPerSheet} stickers</strong> per <strong>{layout.pageSize}</strong> sheet, utilizing {activePrintPercent.toFixed(1)}% of printable surface with minimal margin trim waste.
              </p>
              <div className="text-[11px] text-zinc-400 font-mono">
                Single sticker: {stickerWidth}x{stickerHeight}pt • Sheet: {sheetWidth}x{sheetHeight}pt
              </div>
            </div>
          </div>
        )}

        {/* VIEW 3: Ink Usage & Impression Passes */}
        {activeVisualTab === 'ink' && (
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between text-xs text-zinc-500 font-medium px-1">
              <span>Impression Passes & Ink Consumption Efficiency</span>
              <span className="text-purple-700 font-bold">-{sheetsSaved.toLocaleString()} Machine Passes Saved</span>
            </div>

            <div className="w-full h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={inkEfficiencyData}
                  margin={{ top: 10, right: 20, left: 10, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis 
                    dataKey="metric" 
                    tick={{ fontSize: 11, fill: '#475569', fontWeight: 600 }}
                    axisLine={{ stroke: '#cbd5e1' }}
                    tickLine={false}
                  />
                  <YAxis 
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip 
                    formatter={(value: any, name: any) => [Number(value).toLocaleString(), name]}
                    contentStyle={{ backgroundColor: '#0f172a', borderColor: '#334155', borderRadius: '12px', color: '#f8fafc', fontSize: '12px', fontWeight: 'bold' }}
                  />
                  <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }} />
                  <Bar dataKey="1-Up (Single Pass)" fill="#a855f7" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="Multi-Up Optimized" fill="#3b82f6" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* VIEW 4: Volume Scaling Curve */}
        {activeVisualTab === 'scaling' && (
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between text-xs text-zinc-500 font-medium px-1">
              <span>Cumulative Project ROI Scaling Across Run Volumes</span>
              <span className="text-amber-700 font-bold">Exponential Material & Labor Savings</span>
            </div>

            <div className="w-full h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={volumeCurveData}
                  margin={{ top: 10, right: 20, left: 10, bottom: 5 }}
                >
                  <defs>
                    <linearGradient id="colorSavings" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.4}/>
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0.0}/>
                    </linearGradient>
                    <linearGradient id="colorSingle" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ef4444" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#ef4444" stopOpacity={0.0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis 
                    dataKey="quantity" 
                    tick={{ fontSize: 11, fill: '#475569', fontWeight: 600 }}
                    axisLine={{ stroke: '#cbd5e1' }}
                    tickLine={false}
                  />
                  <YAxis 
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(val) => `${currSym}${val}`}
                  />
                  <Tooltip 
                    formatter={(value: any, name: any) => [`${currSym}${Number(value).toLocaleString()}`, name]}
                    contentStyle={{ backgroundColor: '#0f172a', borderColor: '#334155', borderRadius: '12px', color: '#f8fafc', fontSize: '12px', fontWeight: 'bold' }}
                  />
                  <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }} />
                  <Area type="monotone" dataKey="1-Up Cost" stroke="#ef4444" fillOpacity={1} fill="url(#colorSingle)" />
                  <Area type="monotone" dataKey="Imposition Cost" stroke="#2563eb" fillOpacity={0} />
                  <Area type="monotone" dataKey="Net Savings" stroke="#10b981" fillOpacity={1} fill="url(#colorSavings)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      {/* Optional Cost Parameter Adjustment Accordion */}
      {onUpdateLayout && (
        <div className="border-t border-zinc-800 pt-3">
          <button
            onClick={() => setShowCostSettings(!showCostSettings)}
            className="flex items-center justify-between w-full text-xs font-semibold text-zinc-400 hover:text-zinc-100 cursor-pointer"
          >
            <span className="flex items-center gap-1.5">
              <Sliders className="w-3.5 h-3.5 text-zinc-400" />
              Adjust Production Cost Assumptions (Currency, Paper, Ink, Labor Rates)
            </span>
            {showCostSettings ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {showCostSettings && (
            <div className="mt-3 p-4 bg-zinc-950 border border-zinc-800 rounded-md space-y-3 animate-in fade-in duration-200">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-[11px] font-bold text-zinc-500">Currency</label>
                    <div className="flex items-center gap-1">
                      <span className="text-[9px] font-bold text-zinc-400" title="Live Market Exchange Rate">
                        {activeCode === 'USD' ? exchangeRateString('EUR') : exchangeRateString('USD')}
                      </span>
                      <button 
                        onClick={(e) => { e.stopPropagation(); refreshRates(); }}
                        disabled={isRefreshing}
                        className="p-0.5 hover:bg-zinc-700 text-zinc-400 hover:text-blue-500 rounded transition-colors disabled:opacity-50"
                        title="Sync Latest Market Rates"
                      >
                        <RefreshCw className={`w-2.5 h-2.5 ${isRefreshing ? 'animate-spin' : ''}`} />
                      </button>
                    </div>
                  </div>
                  <select
                    value={activeCode}
                    onChange={(e) => switchCurrency(e.target.value, true)}
                    className="w-full px-2.5 py-1.5 bg-zinc-900 border border-zinc-800 rounded-md text-xs font-bold text-zinc-300 focus:ring-2 focus:ring-blue-600 outline-none cursor-pointer"
                  >
                    {supportedCurrencies.map(c => (
                      <option key={c.code} value={c.code}>
                        {c.flag} {c.code} ({c.symbol})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-[11px] font-bold text-zinc-500 block mb-1">Paper Cost/Sheet</label>
                  <div className="flex items-center gap-1 bg-zinc-900 px-2 py-1 border border-zinc-800 rounded-md">
                    <span className="text-xs font-bold text-zinc-400">{currSym}</span>
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      value={paperCostPerSheet}
                      onChange={(e) => onUpdateLayout('costPerSheet', parseFloat(e.target.value) || 0)}
                      className="w-full text-xs font-bold text-zinc-200 outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-[11px] font-bold text-zinc-500 block mb-1">Ink Cost/Impression</label>
                  <div className="flex items-center gap-1 bg-zinc-900 px-2 py-1 border border-zinc-800 rounded-md">
                    <span className="text-xs font-bold text-zinc-400">{currSym}</span>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={inkCostPerSheet}
                      onChange={(e) => onUpdateLayout('inkCostPerSheet', parseFloat(e.target.value) || 0)}
                      className="w-full text-xs font-bold text-zinc-200 outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-[11px] font-bold text-zinc-500 block mb-1">Press Labor Rate/Hr</label>
                  <div className="flex items-center gap-1 bg-zinc-900 px-2 py-1 border border-zinc-800 rounded-md">
                    <span className="text-xs font-bold text-zinc-400">{currSym}</span>
                    <input
                      type="number"
                      min="0"
                      step="50"
                      value={laborRatePerHour}
                      onChange={(e) => onUpdateLayout('laborRatePerHour', parseFloat(e.target.value) || 0)}
                      className="w-full text-xs font-bold text-zinc-200 outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Substrate Material Library Selection */}
              <div className="pt-2 border-t border-zinc-800/60 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-zinc-400">Active Material:</span>
                  <select
                    value={layout.substrateType || 'standard_vinyl'}
                    onChange={(e) => {
                      const preset = availableSubstrates.find(p => p.id === e.target.value);
                      onUpdateLayout('substrateType', e.target.value);
                      if (preset) {
                        onUpdateLayout('substrateName', preset.name);
                        onUpdateLayout('substrateGsm', preset.weightGsm);
                        if (e.target.value !== 'custom') {
                          onUpdateLayout('costPerSheet', preset.defaultCost);
                        }
                      }
                    }}
                    className="px-2.5 py-1 bg-zinc-900 border border-zinc-800 rounded-md text-xs font-bold text-zinc-200 outline-none focus:ring-2 focus:ring-blue-600"
                  >
                    {availableSubstrates.map(p => (
                      <option key={p.id} value={p.id}>{p.name} ({currSym}{p.defaultCost})</option>
                    ))}
                  </select>
                </div>

                <button
                  type="button"
                  onClick={() => setShowSubstrateModal(true)}
                  className="px-2.5 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-md text-xs font-bold flex items-center gap-1 transition-colors cursor-pointer"
                >
                  <Sliders className="w-3.5 h-3.5 text-blue-600" />
                  <span>Manage Substrates &amp; Custom Rates</span>
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Substrate Customizer Modal */}
      {onUpdateLayout && (
        <SubstrateCustomizerModal
          isOpen={showSubstrateModal}
          onClose={() => setShowSubstrateModal(false)}
          currentSubstrateId={layout.substrateType || 'standard_vinyl'}
          currentCostPerSheet={paperCostPerSheet}
          currencySymbol={currSym}
          onSelectSubstrate={(preset, cost) => {
            onUpdateLayout('substrateType', preset.id);
            onUpdateLayout('substrateName', preset.name);
            onUpdateLayout('substrateGsm', preset.weightGsm);
            if (cost !== undefined) {
              onUpdateLayout('costPerSheet', cost);
            }
          }}
        />
      )}
    </div>
  );
};
